import React, { forwardRef, useContext, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { useTranslation } from 'react-i18next';
import api from 'api';
import useCallbackAsync from 'hooks/useCallbackAsync';
import { User } from 'models/User';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import { getUserFullName } from 'utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

import './RemoveBossModeModal.styles.scss';

type RemoveBossModeModalProps = {
  className?: string;
  bossMode?: User;
  onRemoveBossMode?: () => void;
};

const RemoveBossModeModal: React.ForwardRefRenderFunction<
  ModalRef,
  RemoveBossModeModalProps
> = (props, ref) => {
  const { className, bossMode, onRemoveBossMode } = props;

  const { _id, firstName, lastName } = bossMode || {};

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  useImperativeHandle(ref, () => modal, [modal]);

  const { popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const classes = classNames('remove-boss-mode-modal', className);

  const fullName = getUserFullName(firstName, lastName);

  const [onSubmit] = useCallbackAsync(
    async (values: { removeAccountFromPlatform: boolean }) => {
      const { removeAccountFromPlatform } = values;

      await api.portfolio.admin.removeBossModeAccount(
        _id,
        removeAccountFromPlatform,
      );

      popSuccess(
        t(
          removeAccountFromPlatform
            ? 'accountRemovedFromPlatform'
            : 'accountStripedFromBM',
          {
            fullName,
          },
        ),
      );

      onRemoveBossMode();
      modal?.close();
    },
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('removingBossModeAccount')}
    >
      <Form
        onSubmit={onSubmit}
        render={({ submitting, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <p>
              {t('areYouSureYouWantToStripBM', {
                fullName,
              })}
            </p>

            <Field
              name="removeAccountFromPlatform"
              label={t('removeAccountFromPlatform')}
              type="checkbox"
              component={CheckboxField}
            />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="alert" disabled={submitting}>
                {t('confirm')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(RemoveBossModeModal);
